import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import {
  Train,
  UploadCloud,
  LogOut,
  Bell,
  BellOff,
  Volume2,
  VolumeX,
  Trash2,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  AlertCircle,
  ArrowLeft,
  ArrowUpToLine,
  ArrowDownToLine,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import CountdownWidget from "@/components/CountdownWidget";
import ScheduleTable from "@/components/ScheduleTable";
import UploadCard from "@/components/UploadCard";
import TrainsetTicker from "@/components/TrainsetTicker";
import PnjLogo from "@/components/PnjLogo";
import { useAlarmEngine } from "@/hooks/useAlarmEngine";
import { eventsForRow, formatHM } from "@/lib/scheduleTime";
import { logger } from "@/lib/logger";
import {
  registerServiceWorker,
  subscribePush,
  unsubscribePush,
  sendTestPush,
} from "@/lib/push";

const todayLocalDate = () => {
  // WIB (UTC+7). Use browser local — assumes dispatcher runs in WIB.
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

export default function Dashboard({ onLogout }) {
  const [selectedDate, setSelectedDate] = useState(todayLocalDate());
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(new Date());
  const [editRow, setEditRow] = useState(null); // { row, planId } or "new"
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied",
  );
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [history, setHistory] = useState([]);
  // Bulk selection — set of row IDs currently checked
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Ref untuk mendeteksi day rollover — dibandingkan tiap tick.
  const currentTodayRef = useRef(todayLocalDate());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Register service worker on mount and check existing subscription
  useEffect(() => {
    (async () => {
      await registerServiceWorker();
      if ("serviceWorker" in navigator && "PushManager" in window) {
        try {
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.getSubscription();
          setPushSubscribed(!!sub);
        } catch (err) {
          logger.debug("check push subscription failed", err);
        }
      }
    })();
  }, []);

  const loadPlan = useCallback(async (dateStr) => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/shunting-plans/by-date", {
        params: { date: dateStr },
      });
      setPlan(data.plan);
    } catch (err) {
      console.error("loadPlan failed", err);
      toast.error("Gagal memuat plan");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/shunting-plans");
      setHistory(data.plans || []);
    } catch (err) {
      logger.debug("loadHistory failed", err);
    }
  }, []);

  // Day rollover 00:00 WIB → otomatis pindah ke tanggal baru (kalau user
  // sedang melihat "today"). Jalankan tiap kali `now` update (per detik) —
  // perbandingan string cheap, jadi aman.
  useEffect(() => {
    const nextToday = todayLocalDate();
    if (nextToday === currentTodayRef.current) return;
    const previousToday = currentTodayRef.current;
    currentTodayRef.current = nextToday;
    // Kalau user memang sedang menampilkan hari yang barusan berlalu, geser
    // ke tanggal baru supaya dashboard selalu menunjukkan operational day
    // yang sedang berjalan. Kalau user sengaja melihat tanggal historis
    // (misal seminggu lalu), JANGAN diganggu.
    if (selectedDate === previousToday) {
      setSelectedDate(nextToday);
      toast.info(`Tanggal berpindah ke ${nextToday}`);
    }
    // Refresh history agar hari baru muncul di riwayat.
    loadHistory();
  }, [now, selectedDate, loadHistory]);

  useEffect(() => {
    loadPlan(selectedDate);
    loadHistory();
  }, [selectedDate, loadPlan, loadHistory]);

  const rows = plan?.rows || [];
  // Tanggal yang dijadikan "anchor" untuk perhitungan time-of-day.
  // - Kalau plan real untuk selectedDate: pakai selectedDate.
  // - Kalau plan adalah carried-over (dari H-1): tetap pakai selectedDate karena
  //   dari sudut pandang user, jam 00:07 dst adalah HARI INI di picker.
  const planAnchorDate = selectedDate;
  // Filter out rows whose effective departure was >5 min ago
  // (server auto-cleanup removes them within ~20s, this hides them immediately).
  const visibleRows = useMemo(
    () =>
      rows.filter((r) => {
        const { departureAt } = eventsForRow(r, now, planAnchorDate);
        return (now - departureAt) / 1000 <= 5 * 60;
      }),
    [rows, now, planAnchorDate],
  );

  const { activeAlarm, dismissAlarm, upcoming } = useAlarmEngine({
    rows: visibleRows,
    now,
    soundOn,
    notifPerm,
    planDate: planAnchorDate,
  });

  // Compute next-3 upcoming departure events AFTER the one shown in
  // countdown widget, for the ticker strip. Each entry: { row, at }.
  const nextUpcoming = useMemo(() => {
    const items = visibleRows
      .map((r) => {
        const { departureAt } = eventsForRow(r, now, planAnchorDate);
        return { row: r, at: departureAt };
      })
      .filter((e) => (e.at - now) / 1000 > 0)
      .sort((a, b) => a.at - b.at);
    // Skip the first one (already shown as hero countdown).
    return items.slice(1, 4);
  }, [visibleRows, now, planAnchorDate]);

  // Periodically refetch plan so server-side cleanup is reflected in UI
  useEffect(() => {
    const id = setInterval(() => loadPlan(selectedDate), 60_000);
    return () => clearInterval(id);
  }, [selectedDate, loadPlan]);

  const requestNotif = async () => {
    if (typeof Notification === "undefined") {
      toast.error("Browser tidak mendukung notifikasi");
      return;
    }
    if (pushSubscribed) {
      // Toggle off — unsubscribe
      await unsubscribePush();
      setPushSubscribed(false);
      toast.success("Push notification dinonaktifkan");
      return;
    }
    const res = await subscribePush();
    setNotifPerm(
      typeof Notification !== "undefined" ? Notification.permission : "denied",
    );
    if (res.ok) {
      setPushSubscribed(true);
      toast.success("Push notification aktif — alarm bunyi meski tab tertutup");
    } else if (res.reason === "permission") {
      toast.error("Izin notifikasi ditolak browser");
    }
  };

  const onUploaded = (uploadedPlan) => {
    if (uploadedPlan.plan_date === selectedDate) {
      setPlan(uploadedPlan);
    } else {
      setSelectedDate(uploadedPlan.plan_date);
    }
    loadHistory();
    toast.success(`Berhasil ekstrak ${uploadedPlan.rows.length} baris`);
  };

  const resetPlan = async () => {
    if (
      !window.confirm(
        `Hapus SELURUH jadwal untuk tanggal ${selectedDate}? Tindakan ini tidak bisa dibatalkan.`,
      )
    ) {
      return;
    }
    try {
      await apiClient.delete(
        `/shunting-plans/by-date/${encodeURIComponent(selectedDate)}`,
      );
      setPlan(null);
      loadHistory();
      toast.success("Jadwal berhasil di-reset");
    } catch (err) {
      console.error("reset plan failed", err);
      toast.error("Gagal reset jadwal");
    }
  };

  const clockStr = now.toLocaleTimeString("id-ID", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const dateStr = now.toLocaleDateString("id-ID", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const openNewRow = () => {
    if (!plan) {
      toast.error("Belum ada plan untuk tanggal ini. Upload PDF dulu.");
      return;
    }
    setEditRow({ mode: "new", planId: plan.id, row: emptyRow() });
  };

  const saveRow = async () => {
    if (!editRow) return;
    const payload = editRow.row;
    const tm = /^(\d{2}):(\d{2})$/.exec(payload.start_time || "");
    if (!tm || +tm[1] > 23 || +tm[2] > 59) {
      toast.error("Format waktu harus HH:MM (00:00–23:59)");
      return;
    }
    try {
      if (editRow.mode === "new") {
        await apiClient.post(`/shunting-plans/${editRow.planId}/rows`, payload);
      } else {
        await apiClient.put(
          `/shunting-plans/${editRow.planId}/rows/${payload.id}`,
          payload,
        );
      }
      setEditRow(null);
      await loadPlan(selectedDate);
      toast.success("Tersimpan");
    } catch {
      toast.error("Gagal menyimpan");
    }
  };

  const deleteRow = async (row) => {
    if (!plan) return;
    if (!window.confirm(`Hapus baris ${row.trainset_id} (${row.start_time})?`))
      return;
    try {
      await apiClient.delete(`/shunting-plans/${plan.id}/rows/${row.id}`);
      await loadPlan(selectedDate);
      toast.success("Baris dihapus");
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  const setRowIsNextDay = async (row, nextValue) => {
    if (!plan) return;
    try {
      await apiClient.patch(
        `/shunting-plans/${plan.id}/rows/${row.id}/is-next-day`,
        { is_next_day: nextValue },
      );
      await loadPlan(selectedDate);
      toast.success(
        nextValue
          ? `Baris ${row.trainset_id} → besok pagi`
          : `Baris ${row.trainset_id} → hari ini`,
      );
    } catch {
      toast.error("Gagal memperbarui baris");
    }
  };

  // ----------------- Bulk selection helpers -----------------
  const toggleSelectOne = useCallback((id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((checked) => {
    setSelectedIds(() => {
      if (!checked) return new Set();
      return new Set((plan?.rows || []).map((r) => r.id));
    });
  }, [plan]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Clear selection whenever plan changes (upload/reset/delete flow).
  useEffect(() => {
    setSelectedIds(new Set());
  }, [plan?.id]);

  const bulkDelete = useCallback(async () => {
    if (!plan || selectedIds.size === 0) return;
    if (
      !window.confirm(
        `Hapus ${selectedIds.size} baris terpilih? Aksi ini tidak dapat dibatalkan.`,
      )
    )
      return;
    setBulkBusy(true);
    const ids = Array.from(selectedIds);
    let ok = 0;
    let fail = 0;
    for (const id of ids) {
      try {
        await apiClient.delete(`/shunting-plans/${plan.id}/rows/${id}`);
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setBulkBusy(false);
    if (fail === 0) toast.success(`${ok} baris dihapus`);
    else toast.error(`${ok} baris dihapus, ${fail} gagal`);
    setSelectedIds(new Set());
    await loadPlan(selectedDate);
  }, [plan, selectedIds, selectedDate, loadPlan]);

  const bulkSetIsNextDay = useCallback(
    async (value) => {
      if (!plan || selectedIds.size === 0) return;
      setBulkBusy(true);
      const ids = Array.from(selectedIds);
      let ok = 0;
      let fail = 0;
      for (const id of ids) {
        try {
          await apiClient.patch(
            `/shunting-plans/${plan.id}/rows/${id}/is-next-day`,
            { is_next_day: value },
          );
          ok += 1;
        } catch {
          fail += 1;
        }
      }
      setBulkBusy(false);
      const dest = value ? "besok pagi" : "hari ini";
      if (fail === 0) toast.success(`${ok} baris → ${dest}`);
      else toast.error(`${ok} baris → ${dest}, ${fail} gagal`);
      setSelectedIds(new Set());
      await loadPlan(selectedDate);
    },
    [plan, selectedIds, selectedDate, loadPlan],
  );

  return (
    <div
      className={`relative min-h-screen bg-[#0c1b2a] ${
        activeAlarm ? "edge-flash" : ""
      }`}
      data-testid="dashboard"
    >
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />

      {/* Sticky top bar */}
      <header className="sticky top-0 z-30 border-b border-[#243a52] bg-[#0c1b2a]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-10 h-16 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="dashboard-back-home"
              title="Kembali ke Menu Utama"
              className="h-9 w-9 flex items-center justify-center rounded-md border border-[#243a52] hover:border-[#0ea5e9]/60 hover:bg-[#243a52] text-slate-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div className="h-9 w-9 rounded-md bg-[#034694] flex items-center justify-center">
              <Train className="h-5 w-5" strokeWidth={2.4} />
            </div>
            <div className="hidden md:block">
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
                Shunting Alerts
              </div>
              <div className="text-sm font-display font-semibold">
                Depo Dispatcher Console
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-3 px-4 py-1.5 border border-[#243a52] rounded-full">
              <span className="text-[10px] uppercase tracking-widest text-slate-400">
                WIB
              </span>
              <span
                className="font-mono text-lg font-semibold"
                data-testid="live-clock"
              >
                {clockStr}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSoundOn((s) => !s)}
              className="border border-[#243a52] rounded-full hover:bg-[#1c3550]"
              data-testid="sound-toggle"
            >
              {soundOn ? (
                <Volume2 className="h-4 w-4" />
              ) : (
                <VolumeX className="h-4 w-4 text-slate-500" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={requestNotif}
              className="border border-[#243a52] rounded-full hover:bg-[#1c3550]"
              data-testid="notif-toggle"
              title={
                pushSubscribed
                  ? "Push aktif — klik untuk matikan"
                  : "Aktifkan Web Push (bunyi meski tab tertutup)"
              }
            >
              {pushSubscribed ? (
                <Bell className="h-4 w-4 text-[#289643]" />
              ) : (
                <BellOff className="h-4 w-4 text-[#FFFF00]" />
              )}
            </Button>
            {pushSubscribed && (
              <Button
                variant="ghost"
                size="sm"
                onClick={sendTestPush}
                className="border border-[#243a52] rounded-full hover:bg-[#1c3550] text-xs"
                data-testid="test-push"
                title="Kirim notifikasi uji"
              >
                Test
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="border border-[#243a52] rounded-full hover:bg-[#1c3550]"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-[1600px] px-6 lg:px-10 py-8 space-y-8">
        {/* Header row */}
        <section className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[#289643] mb-2">
              Shunting Plan Monitor
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.02]">
              {dateStr.split(",")[0]},<br />
              <span className="text-slate-300">
                {dateStr.split(",")[1]?.trim()}
              </span>
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 border border-[#243a52] rounded-full px-4 py-2">
              <Label
                htmlFor="date-picker"
                className="text-[10px] uppercase tracking-widest text-slate-400"
              >
                Tanggal
              </Label>
              <input
                id="date-picker"
                data-testid="date-picker"
                type="date"
                className="bg-transparent text-sm font-mono outline-none text-white"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => loadPlan(selectedDate)}
              className="border border-[#243a52] rounded-full"
              data-testid="refresh-plan"
            >
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetPlan}
              className="border border-[#CA2047]/50 text-[#CA2047] rounded-full hover:bg-[#CA2047]/10"
              data-testid="reset-plan"
              title="Hapus seluruh jadwal untuk tanggal terpilih"
            >
              <RotateCcw className="h-4 w-4 mr-2" /> Reset
            </Button>
            <Button
              onClick={openNewRow}
              className="rounded-full bg-[#289643] hover:bg-[#1f7a35] text-white"
              data-testid="add-row-button"
            >
              <Plus className="h-4 w-4 mr-2" /> Tambah Baris
            </Button>
            <UploadCard
              defaultDate={selectedDate}
              onUploaded={onUploaded}
            />
          </div>
        </section>

        {/* Hero countdown — full-width focal point */}
        <section>
          <CountdownWidget
            upcoming={upcoming}
            now={now}
            activeAlarm={activeAlarm}
            onDismiss={dismissAlarm}
            planDate={planAnchorDate}
          />
          <TrainsetTicker items={nextUpcoming} now={now} />
        </section>

        {/* Alarm banner */}
        {activeAlarm && (
          <div
            className="alarm-pulse border border-[#CA2047] bg-[#CA2047]/10 rounded-xl p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between"
            data-testid="active-alarm-banner"
          >
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-md bg-[#CA2047] flex items-center justify-center">
                <AlertCircle className="h-6 w-6" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.24em] text-[#FFFF00]">
                  {activeAlarm.kind === "startup"
                    ? "T-30 · STARTUP REMINDER"
                    : "DEPARTURE NOW"}
                </div>
                <div className="font-display text-2xl font-bold">
                  TS{" "}
                  <span className="font-mono">{activeAlarm.row.trainset_id}</span>{" "}
                  ·{" "}
                  <span className="font-mono">
                    {formatHM(eventsForRow(activeAlarm.row, now).departureAt)}
                  </span>{" "}
                  · {activeAlarm.row.track_origin} →{" "}
                  {activeAlarm.row.track_destination}
                </div>
                <div className="text-xs text-slate-300 mt-1">
                  Operation ID:{" "}
                  <span className="font-mono">{activeAlarm.row.operation_id}</span>
                </div>
              </div>
            </div>
            <Button
              onClick={dismissAlarm}
              data-testid="dismiss-alarm"
              className="rounded-full bg-white text-[#CA2047] hover:bg-slate-100 font-display font-semibold"
            >
              Acknowledge
            </Button>
          </div>
        )}

        {/* Schedule table */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-lg font-semibold">
                Jadwal Shunting
              </h2>
              <div className="text-xs text-slate-400 mt-1">
                {visibleRows.length} baris aktif
                {rows.length !== visibleRows.length && (
                  <span className="text-slate-500">
                    {" "}
                    · {rows.length - visibleRows.length} sudah lewat &gt;5m
                  </span>
                )}
                {" · "}
                {plan?.source_filename ? (
                  <>Sumber: <span className="font-mono">{plan.source_filename}</span></>
                ) : (
                  "Belum ada plan"
                )}
              </div>
            </div>
            <Badge className="bg-[#14283d] border border-[#243a52] text-slate-300 font-mono">
              {selectedDate}
            </Badge>
          </div>

          {/* Bulk action bar — appears when >=1 rows are selected */}
          {selectedIds.size > 0 && (
            <div
              className="mb-3 flex flex-wrap items-center gap-3 rounded-xl border border-[#22d3ee]/40 bg-[#22d3ee]/5 px-4 py-3"
              data-testid="bulk-action-bar"
            >
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full bg-[#22d3ee] px-2 text-xs font-bold text-[#0c1b2a]"
                  data-testid="bulk-count"
                >
                  {selectedIds.size}
                </span>
                <span className="text-sm text-slate-200">
                  baris terpilih
                </span>
              </div>
              <div className="ml-auto flex flex-wrap gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => bulkSetIsNextDay(false)}
                  disabled={bulkBusy}
                  data-testid="bulk-promote"
                  className="border border-[#facc15]/50 text-[#facc15] rounded-full hover:bg-[#facc15]/10"
                  title="Naikkan semua baris terpilih ke hari ini"
                >
                  <ArrowUpToLine className="h-4 w-4 mr-2" /> Naikkan Hari Ini
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => bulkSetIsNextDay(true)}
                  disabled={bulkBusy}
                  data-testid="bulk-demote"
                  className="border border-[#7dd3fc]/50 text-[#7dd3fc] rounded-full hover:bg-[#7dd3fc]/10"
                  title="Turunkan semua baris terpilih ke besok pagi"
                >
                  <ArrowDownToLine className="h-4 w-4 mr-2" /> Turunkan Besok Pagi
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={bulkDelete}
                  disabled={bulkBusy}
                  data-testid="bulk-delete"
                  className="border border-[#CA2047]/50 text-[#CA2047] rounded-full hover:bg-[#CA2047]/10"
                  title="Hapus semua baris terpilih"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Hapus
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  disabled={bulkBusy}
                  data-testid="bulk-cancel"
                  className="border border-[#243a52] text-slate-400 rounded-full hover:bg-[#243a52]"
                >
                  Batal
                </Button>
              </div>
            </div>
          )}

          <ScheduleTable
            rows={visibleRows}
            now={now}
            loading={loading}
            onEdit={(row) =>
              setEditRow({ mode: "edit", planId: plan.id, row: { ...row } })
            }
            onDelete={deleteRow}
            onToggleNextDay={setRowIsNextDay}
            selectedIds={selectedIds}
            onToggleSelect={toggleSelectOne}
            onToggleSelectAll={toggleSelectAll}
            planDate={planAnchorDate}
          />
        </section>

        {/* History */}
        {history.length > 0 && (
          <section>
            <h2 className="font-display text-lg font-semibold mb-4">
              Riwayat Upload
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {history.slice(0, 8).map((h) => (
                <button
                  key={h.id}
                  onClick={() => setSelectedDate(h.plan_date)}
                  data-testid={`history-item-${h.plan_date}`}
                  className="text-left border border-[#243a52] hover:border-[#289643] bg-[#14283d] rounded-lg p-4 transition-colors duration-150"
                >
                  <div className="font-mono text-sm">{h.plan_date}</div>
                  <div className="text-xs text-slate-400 truncate mt-1">
                    {h.source_filename || "manual"}
                  </div>
                  <div className="text-xs text-[#289643] mt-2">
                    {h.row_count} baris
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        <footer
          className="mt-16 pt-6 pb-4 border-t border-[#243a52] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-slate-500"
          data-testid="pnj-footer"
        >
          <div>
            <span className="text-slate-400 font-display font-semibold">
              Shunting Alerts
            </span>{" "}
            · Startup & Departure Alert System · v1.0
          </div>
          <PnjLogo size={36} />
        </footer>
      </main>

      {/* Edit dialog */}
      <Dialog
        open={!!editRow}
        onOpenChange={(o) => !o && setEditRow(null)}
      >
        <DialogContent
          className="bg-[#14283d] border border-[#243a52] text-white"
          data-testid="row-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display">
              {editRow?.mode === "new" ? "Tambah Baris" : "Edit Baris"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Start time adalah jam keberangkatan dari PDF. Startup reminder
              otomatis dihitung 30 menit sebelumnya.
            </DialogDescription>
          </DialogHeader>
          {editRow && (
            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Start Time (HH:MM)"
                testid="row-start-time"
                value={editRow.row.start_time}
                onChange={(v) =>
                  setEditRow((s) => ({ ...s, row: { ...s.row, start_time: v } }))
                }
                mono
                placeholder="05:30"
              />
              <Field
                label="Trainset ID"
                testid="row-trainset-id"
                value={editRow.row.trainset_id}
                onChange={(v) =>
                  setEditRow((s) => ({ ...s, row: { ...s.row, trainset_id: v } }))
                }
                mono
                placeholder="TS-01"
              />
              <Field
                label="Track Origin"
                testid="row-track-origin"
                value={editRow.row.track_origin}
                onChange={(v) =>
                  setEditRow((s) => ({
                    ...s,
                    row: { ...s.row, track_origin: v },
                  }))
                }
                placeholder="Stabling 3"
              />
              <Field
                label="Track Destination"
                testid="row-track-destination"
                value={editRow.row.track_destination}
                onChange={(v) =>
                  setEditRow((s) => ({
                    ...s,
                    row: { ...s.row, track_destination: v },
                  }))
                }
                placeholder="LB Line"
              />
              <Field
                label="Operation ID"
                testid="row-operation-id"
                value={editRow.row.operation_id}
                onChange={(v) =>
                  setEditRow((s) => ({
                    ...s,
                    row: { ...s.row, operation_id: v },
                  }))
                }
                mono
                placeholder="OP-101"
              />
              <Field
                label="Note"
                testid="row-note"
                value={editRow.row.note || ""}
                onChange={(v) =>
                  setEditRow((s) => ({ ...s, row: { ...s.row, note: v } }))
                }
                placeholder="opsional"
              />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setEditRow(null)}
              className="rounded-full border border-[#243a52]"
              data-testid="row-cancel"
            >
              Batal
            </Button>
            <Button
              onClick={saveRow}
              className="rounded-full bg-[#289643] hover:bg-[#1f7a35]"
              data-testid="row-save"
            >
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const emptyRow = () => ({
  start_time: "",
  track_origin: "",
  track_destination: "",
  operation_id: "",
  trainset_id: "",
  note: "",
  is_next_day: false,
});

function Field({ label, value, onChange, testid, mono, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-widest text-slate-400">
        {label}
      </Label>
      <Input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`bg-[#0c1b2a] border-[#243a52] focus-visible:ring-[#289643] ${
          mono ? "font-mono" : ""
        }`}
      />
    </div>
  );
}
